import React, { useEffect, useState, useCallback } from 'react';
import {
  Clock, CheckCircle, AlertCircle, Loader2, Eye, Users,
  RefreshCw, Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getBatches, deleteBatch } from '../services/prospectingService';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/components/common/Toast';
import { Pagination } from '@/components/common/Pagination';
import type { ProspectBatch } from '../services/prospectingService';

const STATUS_CONFIG: Record<string, { icon: any; cls: string; label: string; spin?: boolean }> = {
  PENDING:  { icon: Clock,       cls: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800',         label: 'Pending' },
  RUNNING:  { icon: Loader2,     cls: 'text-blue-600 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800',                   label: 'Running', spin: true },
  REVIEW:   { icon: Eye,         cls: 'text-primary bg-primary/5 border-primary/20',                                                          label: 'Review' },
  APPROVED: { icon: CheckCircle, cls: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800',     label: 'Submitted' },
  FAILED:   { icon: AlertCircle, cls: 'text-red-600 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800',                        label: 'Failed' },
};

const CONNECTOR_LABELS: Record<string, string> = {
  tavily: 'Tavily', apollo_enrichment: 'Apollo', apollo_email: 'Apollo',
  hunter: 'Hunter.io', openrouter: 'OpenRouter', clearbit: 'Clearbit',
  linkedin_nav: 'LinkedIn', vibeprospecting: 'VibeProspecting',
  lusha: 'Lusha', custom_webhook: 'Webhook',
};

const PAGE_SIZE = 10;
type Tab = 'active' | 'submitted';

interface Props {
  externalBatches?: ProspectBatch[];
  loaded?: boolean;
  onRefresh?: () => void;
}

export default function BatchListPage({ externalBatches, loaded, onRefresh }: Props) {
  const navigate    = useNavigate();
  const [allBatches, setAllBatches]       = useState<ProspectBatch[]>([]);
  const [loading, setLoading]             = useState(true);
  const [deletingId, setDeletingId]       = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [tab, setTab]                     = useState<Tab>('active');
  const [page, setPage]                   = useState(1);
  const { canDelete } = usePermissions('prospecting');
  const { addToast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    getBatches(1, 200)
      .then(r => setAllBatches(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (externalBatches !== undefined) {
      setAllBatches(externalBatches);
      setLoading(false);
    } else {
      load();
    }
  }, [externalBatches, load]);

  const refresh = () => { if (onRefresh) onRefresh(); else load(); };

  const handleDelete = async (batchId: string) => {
    setDeletingId(batchId);
    try {
      await deleteBatch(batchId);
      setAllBatches(prev => prev.filter(b => b.id !== batchId));
      if (onRefresh) onRefresh();
    } catch (err: any) {
      addToast({ type: 'error', title: 'Delete failed', description: err.response?.data?.error || 'Could not delete this batch.' });
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  // ── Split into tabs ───────────────────────────────────────────────────────
  const activeBatches    = allBatches.filter(b => b.status !== 'APPROVED');
  const submittedBatches = allBatches.filter(b => b.status === 'APPROVED');
  const displayBatches   = tab === 'submitted' ? submittedBatches : activeBatches;

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(displayBatches.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = displayBatches.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const goTab = (t: Tab) => { setTab(t); setPage(1); };

  if (loading && !loaded) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-3">

      {/* ── Header: tabs + refresh ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
          {([
            { id: 'active',    label: 'Recent Runs',  count: activeBatches.length    },
            { id: 'submitted', label: 'Submitted',     count: submittedBatches.length },
          ] as { id: Tab; label: string; count: number }[]).map(t => (
            <button key={t.id} onClick={() => goTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                tab === t.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}>
              {t.label}
              {t.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  tab === t.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>
        <button onClick={refresh}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <RefreshCw className="h-3 w-3" />Refresh
        </button>
      </div>

      {/* ── Empty state ── */}
      {displayBatches.length === 0 && (
        <div className="text-center py-12 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
          {tab === 'submitted'
            ? 'No submitted batches yet. Approve prospects from a Review batch to see them here.'
            : 'No runs yet. Configure connectors and start a prospecting run.'}
        </div>
      )}

      {/* ── Batch rows ── */}
      {paginated.map(batch => {
        const cfg         = STATUS_CONFIG[batch.status] || STATUS_CONFIG.PENDING;
        const Icon        = cfg.icon;
        const connectors  = (batch.criteria as any)?.connectors || {};
        const usedLabels  = [...new Set(
          Object.values(connectors)
            .filter((v: any) => v && v !== 'none' && v !== '')
            .map((v: any) => CONNECTOR_LABELS[v as string] || v)
        )] as string[];
        const isDeleting   = deletingId === batch.id;
        const isConfirming = confirmDelete === batch.id;
        const buyingSignals = (batch.criteria as any)?.buyingSignals || [];
        const intentTopics  = (batch.criteria as any)?.intentTopics  || [];

        return (
          <div key={batch.id}
            className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card hover:border-primary/20 hover:shadow-sm transition-all group">

            {/* Status */}
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border shrink-0 ${cfg.cls}`}>
              <Icon className={`h-3 w-3 ${cfg.spin ? 'animate-spin' : ''}`} />
              {cfg.label}
            </span>

            {/* Main info */}
            <div className="flex-1 min-w-0 space-y-1.5">
              <p className="text-sm font-semibold truncate">
                {(batch as any).label
                  ? (batch as any).label
                  : (() => {
                      const crit = (batch.criteria as any) || {};
                      const parts = [crit.targetIndustry, crit.region].filter(Boolean);
                      const kw = (crit.keywords || []).slice(0, 2).join(', ');
                      const size = crit.sizeMin && crit.sizeMax ? ` · ${crit.sizeMin}–${crit.sizeMax} emp` : '';
                      return parts.join(' · ') + (kw ? ` · "${kw}"` : '') + size || 'Unnamed Run';
                    })()
                }
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground">
                  {new Date(batch.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {' · '}{batch.triggeredBy?.name || 'Unknown'}
                </span>
                {usedLabels.map(v => (
                  <span key={v} className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium">{v}</span>
                ))}
              </div>
              {/* Buying signals / intent preview */}
              {(buyingSignals.length > 0 || intentTopics.length > 0) && (
                <div className="flex flex-wrap gap-1">
                  {buyingSignals.slice(0, 2).map((s: string) => (
                    <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400">
                      ⚡ {s.length > 30 ? s.slice(0, 28) + '…' : s}
                    </span>
                  ))}
                  {intentTopics.slice(0, 1).map((t: string) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400">
                      🎯 {t.split(':').pop()?.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Counts */}
            <div className="hidden sm:flex flex-col items-end gap-0.5 text-xs text-muted-foreground shrink-0">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {batch._count?.prospects ?? batch.totalFound ?? 0} found
              </span>
              {(batch.totalApproved ?? 0) > 0 && (
                <span className="text-emerald-600 font-medium">{batch.totalApproved} approved</span>
              )}
              {(batch.totalRejected ?? 0) > 0 && (
                <span className="text-red-500">{batch.totalRejected} rejected</span>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              {batch.status === 'REVIEW' && (
                <button onClick={() => navigate(`/prospecting/batches/${batch.id}`)}
                  className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors">
                  Review
                </button>
              )}
              {batch.status === 'APPROVED' && (
                <button onClick={() => navigate(`/prospecting/batches/${batch.id}`)}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors">
                  View
                </button>
              )}
              {batch.status === 'RUNNING' && (
                <button onClick={() => navigate(`/prospecting/batches/${batch.id}`)}
                  className="px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-medium hover:bg-muted/80 transition-colors">
                  Watch
                </button>
              )}
              {batch.status === 'FAILED' && (
                <span className="text-xs text-red-500 font-medium">Failed</span>
              )}

              {canDelete && (
                isConfirming ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Delete?</span>
                    <button onClick={() => handleDelete(batch.id)} disabled={isDeleting}
                      className="px-2 py-1 rounded text-xs bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                      {isDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes'}
                    </button>
                    <button onClick={() => setConfirmDelete(null)}
                      className="px-2 py-1 rounded text-xs border border-border text-muted-foreground hover:bg-muted">
                      No
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDelete(batch.id)}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 opacity-0 group-hover:opacity-100 transition-all"
                    title="Delete batch">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )
              )}
            </div>
          </div>
        );
      })}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <Pagination
          currentPage={safePage}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          totalItems={displayBatches.length}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
